import React from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminPageShell from '../components/AdminPageShell';
import AdminTabs from '../components/AdminTabs';
import SessionsView from './legislature/SessionsView';
import { HelpTrigger } from '../../help';

const TABS = [{ key: 'sessions', label: 'Sessions' }] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Legislature — registries describing the General Assembly itself, as opposed to
 * Elections, which describes the electoral cycle around it.
 *
 * Launches with Sessions as its only tab (D-11). It is a separate page rather
 * than a fifth Elections tab because a legislative session is not an election
 * cycle: they are different periods with different boundaries, and folding them
 * together is how the app ended up with six competing ideas of "current" in the
 * first place. Leadership roles are the natural next tenant —
 * leadership_role_mappings already carries session_year and is admin-maintained.
 *
 * Plan: Documentation/LEGISLATIVE_SESSION_MODEL_PRD.md §5.R11
 */
const LegislaturePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey = TABS.some((tab) => tab.key === requestedTab)
    ? (requestedTab as TabKey)
    : 'sessions';

  return (
    <AdminPageShell
      title="Legislature"
      description="Legislative sessions — the definition of what is current across the app"
      showHeading={false}
    >
      <div className="flex items-center gap-2">
        <AdminTabs
          tabs={[...TABS]}
          activeKey={activeTab}
          onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
        />
        <HelpTrigger contentKey="admin-sessions" />
      </div>

      {activeTab === 'sessions' && <SessionsView />}
    </AdminPageShell>
  );
};

export default LegislaturePage;
